import React from 'react';
import { makeStyles } from 'tss-react/mui';

export interface ICustomIconProps {
  disabled?: boolean;
  pathString: string;
  viewBox: string;
  'data-testid'?: string;
}

/**
 * Safari does not work out the height of an svg that only carries a viewBox, so these icons
 * collapse to nothing inside the width-only flex containers they live in. Handing the element the
 * viewBox aspect ratio lets every browser resolve the axis the parent does not set.
 */
export function getAspectRatio(viewBox: string): string | undefined {
  const [, , width, height] = viewBox
    .trim()
    .split(/[\s,]+/)
    .map(Number);

  if (!width || !height) return undefined;

  return `${width} / ${height}`;
}

const useStyles = makeStyles<{ aspectRatio?: string }>()((theme, { aspectRatio }) => ({
  icon: {
    width: '100%',
    height: 'auto',
    aspectRatio,
    '& path': {
      fill: theme.palette.text.primary
    }
  },
  disabled: {
    '& path': {
      opacity: 0.5,
      fill: theme.palette.text.disabled
    }
  }
}));

export function CustomIcon({ viewBox, pathString, disabled = false, ...props }: ICustomIconProps): React.ReactElement {
  const { classes } = useStyles({ aspectRatio: getAspectRatio(viewBox) });

  return (
    <svg
      className={`${classes.icon} ${disabled ? classes.disabled : ''}`}
      viewBox={viewBox}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      data-testid={props['data-testid']}
    >
      <g stroke="none" fill="none" fillRule="evenodd">
        <path d={pathString}></path>
      </g>
    </svg>
  );
}
